import { useTranslation } from "react-i18next";
import useScheduleRangeOnRoute from "@/pages/human-resources/time-management/hooks/useScheduleRangeOnRoute.ts";
import useDisplayOptionsState from "@/pages/human-resources/time-management/model/display-options/useDisplayOptionsState.ts";
import useDisplayOptionsDispatch from "@/pages/human-resources/time-management/model/display-options/useDisplayOptionsDispatch.ts";
import { useCallback, useState } from "react";
import type { Item } from "@/shared/components/generic/items.ts";

interface Controller {
  title: string;
  items: Item<DisplayOptionType>[];
  isSelectedItem: (item: Item<DisplayOptionType>) => boolean;
  toggleItem: (item: Item<DisplayOptionType>) => void;
  folded: boolean;
  setFolded: (f: boolean) => void;
}

type DisplayOptionType =
  | "toggleCondensedView"
  | "toggleShowDayLabel"
  | "toggleShowWorkDuration"
  | "toggleShowPositions"
  | "toggleShowBreaks";

interface DisplayOption {
  type: DisplayOptionType;
  label: string;
  visible: boolean;
}

export default function useDisplayTogglesController(): Controller {
  const { t } = useTranslation(["humanResources"], {
    keyPrefix: "timeManagement.scheduleDisplayOptions.displayToggles",
  });
  const range = useScheduleRangeOnRoute();
  const optionsState = useDisplayOptionsState();
  const { dispatch } = useDisplayOptionsDispatch();

  const [folded, setFolded] = useState(false);

  const options: DisplayOption[] = [
    {
      type: "toggleCondensedView",
      label: t("condensedView"),
      visible: true,
    },
    {
      type: "toggleShowDayLabel",
      label: t("dayLabel"),
      visible: true,
    },
    {
      type: "toggleShowWorkDuration",
      label: t("workDuration"),
      visible: range === "MONTH",
    },
    {
      type: "toggleShowPositions",
      label: t("positions"),
      visible: range !== "MONTH",
    },
    {
      type: "toggleShowBreaks",
      label: t("breaks"),
      visible: range !== "MONTH",
    },
  ];

  const items = options
    .filter((o) => o.visible)
    .map((o) => ({
      key: o.type,
      label: o.label,
      data: o.type,
    }));

  const isSelectedItem = useCallback(
    (item: Item<DisplayOptionType>): boolean => {
      if (item.data === undefined) return false;
      switch (item.data) {
        case "toggleCondensedView":
          return optionsState.showCondensedView;
        case "toggleShowDayLabel":
          return optionsState.showDayLabel;
        case "toggleShowWorkDuration":
          return optionsState.showWorkDuration;
        case "toggleShowPositions":
          return optionsState.showPositions;
        case "toggleShowBreaks":
          return optionsState.showBreaks;
      }
    },
    [
      optionsState.showBreaks,
      optionsState.showCondensedView,
      optionsState.showDayLabel,
      optionsState.showWorkDuration,
      optionsState.showPositions,
    ],
  );

  const toggleOption = useCallback(
    (item: Item<DisplayOptionType>): void => {
      if (item.data !== undefined) dispatch({ type: item.data });
    },
    [dispatch],
  );

  return {
    title: t("title"),
    items,
    isSelectedItem,
    toggleItem: toggleOption,
    folded,
    setFolded,
  };
}
